import React from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Field, NumberField } from "@/pages/lavoratori/Fields";
import CedoliniStorico from "@/pages/lavoratori/CedoliniStorico";

const CONTRATTI = [
  "Tempo indeterminato",
  "Tempo determinato",
  "Apprendistato",
  "Collaborazione",
  "Partita IVA",
  "Altro",
];

/**
 * Modale grande di creazione/modifica lavoratore.
 *
 * Props:
 *   isEditing   – bool, cambia titolo e testo del bottone
 *   form        – oggetto dati corrente
 *   setForm     – setter React
 *   onClose     – callback su chiudi/Annulla
 *   onSave      – callback su Salva (riceve il form)
 *   saving      – bool, disabilita il bottone
 */
export default function LavoratoreForm({ isEditing, form, setForm, onClose, onSave, saving }) {
  const handleSave = () => {
    onSave(form);
  };
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lavoratore-modal"
    >
      <div
        className="bg-white w-full max-w-3xl border border-slate-200 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display font-bold text-xl">
              {isEditing ? "Modifica anagrafica lavoratore" : "Nuovo lavoratore"}
            </h2>
            {isEditing && (
              <p className="text-xs text-slate-500 mt-1">
                Lo storico di cantieri, rapportini, ferie e presenze resta invariato.
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="btn-close-modal">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Dati anagrafici
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome" testid="input-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} />
              <Field label="Cognome" testid="input-cognome" value={form.cognome} onChange={(v) => setForm({ ...form, cognome: v })} />
              <Field label="Telefono" testid="input-telefono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
              <Field label="Email" testid="input-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </div>
          </section>

          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Inquadramento
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Mansione" testid="input-mansione" value={form.mansione} onChange={(v) => setForm({ ...form, mansione: v })} />
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Contratto</label>
                <select
                  data-testid="input-contratto"
                  value={form.contratto}
                  onChange={(e) => setForm({ ...form, contratto: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                >
                  {CONTRATTI.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <Field label="Società" testid="input-societa" value={form.societa} onChange={(v) => setForm({ ...form, societa: v })} />
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="input-active"
                    checked={form.active !== false}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-semibold">Attivo</span>
                </label>
              </div>
            </div>
          </section>

          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Costi & retribuzione
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <NumberField label="Retribuzione €/h" step="0.1" value={form.retribuzione_oraria} onChange={(v) => setForm({ ...form, retribuzione_oraria: v })} />
              <NumberField label="Costo orario aziendale €/h" step="0.1" value={form.costo_orario} onChange={(v) => setForm({ ...form, costo_orario: v })} testid="input-costo-orario" />
              <NumberField label="Contributi %" step="0.1" value={form.contributi_pct} onChange={(v) => setForm({ ...form, contributi_pct: v })} />
              <NumberField label="TFR %" step="0.1" value={form.tfr_pct} onChange={(v) => setForm({ ...form, tfr_pct: v })} />
              <NumberField label="Mensilità aggiuntive %" step="0.1" value={form.mensilita_aggiuntive_pct} onChange={(v) => setForm({ ...form, mensilita_aggiuntive_pct: v })} />
              <NumberField
                label="Trasferta €/giornata (non tassata)"
                step="0.5"
                value={form.trasferta || 0}
                onChange={(v) => setForm({ ...form, trasferta: v })}
                testid="input-trasferta"
              />
              <NumberField
                label="Paga base mensile € (default)"
                step="1"
                value={form.paga_base_default || 0}
                onChange={(v) => setForm({ ...form, paga_base_default: v })}
                testid="input-paga-base-default"
              />
              <NumberField
                label="Costo ora straordinaria €/h (default)"
                step="0.1"
                value={form.costo_ora_straordinaria_default || 0}
                onChange={(v) => setForm({ ...form, costo_ora_straordinaria_default: v })}
                testid="input-costo-straord-default"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              La <b>Paga base mensile</b> e il <b>Costo ora straordinaria</b> impostati qui
              sono valori di default: verranno pre-compilati automaticamente per ogni nuovo
              mese nello <b>Storico Cedolini</b>. Puoi comunque modificarli mese per mese
              senza che i mesi precedenti vengano toccati.
            </p>

            {/* Flag per aggiungere automaticamente la trasferta ai Costi Fissi / Variabili */}
            <div className="mt-4 p-3 border border-slate-200 rounded-sm bg-slate-50 space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Aggancia la trasferta ai costi aziendali
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="input-trasferta-in-costi-fissi"
                  checked={!!form.trasferta_in_costi_fissi}
                  onChange={(e) => setForm({ ...form, trasferta_in_costi_fissi: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>
                  Aggiungi come voce ricorrente in <strong>Costi Fissi</strong> (mensile)
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="input-trasferta-in-costi-variabili"
                  checked={!!form.trasferta_in_costi_variabili}
                  onChange={(e) => setForm({ ...form, trasferta_in_costi_variabili: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>
                  Aggiungi come voce una tantum in <strong>Costi Variabili</strong>
                </span>
              </label>
              <p className="text-[11px] text-slate-500 mt-1">
                La voce viene creata/aggiornata/eliminata in automatico al salvataggio.
                Se l'importo trasferta è 0, non viene creata alcuna voce.
              </p>
            </div>
          </section>

          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Ferie & permessi contrattuali (in ore)
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <NumberField
                  label="Ore ferie annuali"
                  step="0.5"
                  value={form.ore_ferie_annuali ?? 0}
                  onChange={(v) => setForm({ ...form, ore_ferie_annuali: Number(v) || 0 })}
                  testid="input-ore-ferie"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Inserisci il monte ore ferie annue (qualsiasi valore, es. 208 h = 26 gg × 8 h).
                </div>
              </div>
              <div>
                <NumberField
                  label="Ore permessi annuali (ROL)"
                  step="1"
                  value={form.ore_permesso_annuali}
                  onChange={(v) => setForm({ ...form, ore_permesso_annuali: v })}
                  testid="input-ore-permessi"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Monte ore permessi retribuiti / anno
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Note interne</div>
            <textarea
              data-testid="input-note"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              placeholder="Es. certificazioni, patenti, allergie, dispositivi consegnati…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </section>

          {isEditing && form.lavoratore_id && (
            <CedoliniStorico lavoratoreId={form.lavoratore_id} />
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="btn-save-lavoratore"
            className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
          >
            {saving ? "Salvataggio…" : isEditing ? "Salva modifiche" : "Crea lavoratore"}
          </button>
        </div>
      </div>
    </div>
  );
}
